import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { ListAdditionalChargesSortParams } from 'store/invoice-config/types';
import { SortDirection } from 'utils/tableHelpers';

export enum InvoiceChargesParamKeys {
    QUERY = 'query',
    ARCHIVED = 'archived',
    PAGE = 'page',
    SIZE = 'size',
    SORT = 'sort',
    SORT_DIR = 'sortDirection',
}

const useInvoiceChargesParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const archivedParam = searchParams.get(InvoiceChargesParamKeys.ARCHIVED);
    const initialArchived = archivedParam == null ? false : archivedParam === 'true';
    const initialQuery = searchParams.get(InvoiceChargesParamKeys.QUERY);
    const initialPage = Number(searchParams.get(InvoiceChargesParamKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(InvoiceChargesParamKeys.SIZE)) || 10;
    const initialSort = searchParams.get(InvoiceChargesParamKeys.SORT) as ListAdditionalChargesSortParams;
    const initialSortDir = (searchParams.get(InvoiceChargesParamKeys.SORT_DIR) as 'ASC' | 'DESC') || 'DESC';

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [archived, setArchived] = useState<boolean | undefined>(initialArchived);
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);

    const [sortState, setSortState] = useState<{
        sort: ListAdditionalChargesSortParams | undefined;
        sortDirection: SortDirection | undefined;
    }>({ sort: initialSort, sortDirection: initialSortDir });

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: any) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: string, val?: string | null) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            apply(InvoiceChargesParamKeys.QUERY, next.query);
            apply(InvoiceChargesParamKeys.ARCHIVED, next.archived === undefined ? 'false' : String(next.archived));
            apply(InvoiceChargesParamKeys.PAGE, next?.page?.toString());
            apply(InvoiceChargesParamKeys.SIZE, next?.size?.toString());
            apply(InvoiceChargesParamKeys.SORT, next.sort);
            apply(InvoiceChargesParamKeys.SORT_DIR, next.sortDirection);

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            archived,
            query: debouncedQuery,
            page,
            size,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
        });
    }, [archived, debouncedQuery, page, size, sortState.sort, sortState.sortDirection, writeParams]);

    const appliedParams = useMemo(
        () => ({
            query: debouncedQuery || undefined,
            archived,
            page,
            size,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
        }),
        [archived, debouncedQuery, page, size, sortState.sort, sortState.sortDirection]
    );

    const clearFilter = useCallback((key: string) => {
        switch (key) {
            case InvoiceChargesParamKeys.QUERY: {
                setQueryInputVal('');
                break;
            }

            default: {
                break;
            }
        }
    }, []);

    const clearAll = useCallback(() => {
        setQueryInputVal('');
        setPage(0);
    }, []);

    return {
        appliedParams,
        queryInputVal,
        setQueryInputVal,
        setArchived,
        setPage,
        setSize,
        setSortState,
        clearFilter,
        clearAll,
    };
};

export default useInvoiceChargesParams;
